import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { btnGold, errMsg, inp, lbl } from "@/components/attendance/ui";

const COPY = {
  attendance: { eyebrow: "HOH Attendance", next: "/attendance/dashboard?welcome=1" },
  projection: { eyebrow: "HOH Worship Projection", next: "/projection/console?welcome=1" },
};

export default function AttendanceRegister({ product = "attendance" }) {
  const copy = COPY[product];
  const { user } = useAuth();
  const [types, setTypes] = useState(["Church"]);
  const [f, setF] = useState({ name: "", email: "", password: "", org_name: "", org_type: "Church", address: "", authority_email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/attendance/meta").then((r) => setTypes(r.data.org_types)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const base = product === "projection" ? { ...f, authority_email: f.authority_email || (user ? user.email : f.email) } : f;
      const body = user ? { ...base, name: null, email: null, password: null } : base;
      await api.post("/attendance/register", body);
      window.location.assign(copy.next);
    } catch (err) { toast.error(errMsg(err, "Registration failed")); setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="attendance-register">
      <div className="label-eyebrow mb-3">{copy.eyebrow}</div>
      <h1 className="font-serif-display text-4xl text-white">Register your organisation</h1>
      <p className="text-neutral-400 mt-3 text-sm">You'll start your 30-day free trial on the next step.</p>
      <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-4">
        {user ? (
          <div className="sm:col-span-2 luxury-card rounded-lg p-4 text-sm text-neutral-300" data-testid="att-reg-signed-in">
            Registering as <span className="text-[#D4AF37]">{user.email}</span>. This account becomes the organisation administrator.
          </div>
        ) : (
          <>
            <Field label="Your name *" k="name" f={f} set={set} required />
            <Field label="Your email *" k="email" type="email" f={f} set={set} required />
            <div className="sm:col-span-2"><Field label="Password * (min 8 characters)" k="password" type="password" f={f} set={set} required minLength={8} /></div>
            <p className="sm:col-span-2 text-xs text-neutral-500">Already have a HOH_Vibes account? <Link className="text-[#D4AF37]" to={`/login?next=/${product}/register`}>Sign in first</Link>.</p>
          </>
        )}
        <div className="sm:col-span-2 h-px bg-white/10 my-2" />
        <Field label="Organisation name *" k="org_name" f={f} set={set} required />
        <div>
          <label className={lbl}>Organisation type *</label>
          <select data-testid="att-reg-org_type" className={inp} value={f.org_type} onChange={set("org_type")}>{types.map((t) => <option key={t}>{t}</option>)}</select>
        </div>
        <div className="sm:col-span-2"><Field label="Address" k="address" f={f} set={set} /></div>
        <p className="sm:col-span-2 text-[11px] text-neutral-500">One organisation account covers HOH Attendance and HOH Hymn Projection — subscribe to either, or to the Bundle.</p>
        {product === "attendance" && (
          <div className="sm:col-span-2">
            <Field label="Report recipient email * (pastor, trustee or authority)" k="authority_email" type="email" f={f} set={set} required />
            <p className="text-[11px] text-neutral-500 mt-1">Final Excel attendance reports are emailed here when each event closes.</p>
          </div>
        )}
        <p className="sm:col-span-2 text-[11px] text-neutral-500">By registering you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>. We only store attendance counts, event details and usher names/emails.</p>
        <button disabled={busy} data-testid="att-reg-submit" className={`${btnGold} sm:col-span-2 py-3.5`}>{busy && <Loader2 size={14} className="animate-spin" />} Register & continue</button>
      </form>
    </div>
  );
}

function Field({ label, k, f, set, type = "text", ...rest }) {
  return (
    <div>
      <label className={lbl}>{label}</label>
      <input data-testid={`att-reg-${k}`} type={type} className={inp} value={f[k]} onChange={set(k)} {...rest} />
    </div>
  );
}
